import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { getLocalStorage } from 'helpers/storage';

import './CheckoutMobileSummaryBar.scss';

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
const formatPercentOff = (percent) => {
  const value = Number(percent);
  if (!Number.isFinite(value) || value <= 0) return null;
  return `${value}% OFF`;
};

const computePaymentRows = ({ paymentMethods, subtotal, coupon, subTotEnv }) => {
  if (!Array.isArray(paymentMethods) || paymentMethods.length === 0) return [];

  const discountRaw = getLocalStorage('discount');
  const discount = discountRaw === null ? null : Number.parseFloat(discountRaw);
  const subtotalValue = Number.parseFloat(subtotal || 0);
  const couponAmount = coupon?.percentage
    ? (Number(coupon.percentage) / 100) * subtotalValue
    : Number(coupon?.amount || 0);

  return paymentMethods.map((method) => {
    const methodPercent = typeof method.percent === 'number' ? method.percent : Number(method.percent || 0);
    const label = method.name || method.code || 'Medio de pago';
    const baseAuto = (method.allow_discount && discount !== null) ? discount : subtotalValue;
    const baseWithCoupon = Math.max(0, baseAuto - couponAmount);
    const amount = +((baseWithCoupon + Number(subTotEnv || 0)) * (1 - Number(methodPercent || 0))).toFixed(2);

    return {
      key: method.code || label,
      label,
      amount,
      isAutoAllowed: !!method.allow_discount,
    };
  });
};

const computeTotals = ({ subtotal, coupon, subTotEnv }) => {
  const subtotalValue = Number.parseFloat(subtotal || 0);
  const autoDiscountValueRaw = getLocalStorage('discount');
  const autoDiscountValue = autoDiscountValueRaw === null ? null : Number.parseFloat(autoDiscountValueRaw);

  const couponAmount = coupon?.percentage
    ? (Number(coupon.percentage) / 100) * subtotalValue
    : Number(coupon?.amount || 0);

  const baseWithoutShipping = autoDiscountValue === null
    ? subtotalValue - couponAmount
    : coupon?.percentage
      ? subtotalValue - couponAmount
      : autoDiscountValue - couponAmount;

  const total = Math.max(0, baseWithoutShipping) + Number(subTotEnv || 0);

  return {
    subtotalValue,
    couponAmount,
    hasAutoDiscount: autoDiscountValue !== null,
    autoPercent: getLocalStorage('percentage'),
    total,
  };
};

const getDiscountScopeNote = (paymentMethods) => {
  if (!Array.isArray(paymentMethods) || paymentMethods.length === 0) return null;

  const hasRestricted = paymentMethods.some((m) => !m.allow_discount);
  if (!hasRestricted) return null;

  const allowed = paymentMethods
    .filter((m) => !!m.allow_discount)
    .map((m) => m.name || m.code || 'Medio habilitado')
    .filter(Boolean);

  if (allowed.length === 0) return 'Según medio de pago';

  const head = allowed.slice(0, 2).join(', ');
  const more = allowed.length > 2 ? ` +${allowed.length - 2}` : '';
  return `Solo en: ${head}${more}`;
};

const CheckoutMobileSummaryBar = ({
  subtotal,
  coupon,
  subTotEnv,
  shippingText,
  paymentMethods,
  children,
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const totals = useMemo(
    () => computeTotals({ subtotal, coupon, subTotEnv }),
    [subtotal, subTotEnv, coupon],
  );

  const paymentRows = useMemo(
    () => computePaymentRows({
      paymentMethods,
      subtotal,
      coupon,
      subTotEnv,
    }),
    [paymentMethods, subtotal, subTotEnv, coupon],
  );

  const primaryPaymentRow = useMemo(() => {
    if (paymentRows.length === 0) return null;
    return paymentRows.reduce((min, row) => (row.amount < min.amount ? row : min), paymentRows[0]);
  }, [paymentRows]);

  const previewPaymentRows = useMemo(() => paymentRows.slice(0, 2), [paymentRows]);

  const discountScopeNote = useMemo(
    () => getDiscountScopeNote(paymentMethods),
    [paymentMethods],
  );

  const discountLabel = () => {
    const autoOff = formatPercentOff(totals.autoPercent);
    if (coupon) {
      if (coupon.percentage) return autoOff ? `Cupón ${coupon.percentage}% + ${autoOff}` : `Cupón ${coupon.percentage}%`;
      if (coupon.amount) return autoOff ? `Cupón ${formatMoney(coupon.amount)} + ${autoOff}` : `Cupón ${formatMoney(coupon.amount)}`;
      return 'Cupón aplicado';
    }
    if (totals.hasAutoDiscount && autoOff) return autoOff;
    if (totals.hasAutoDiscount) return 'Descuento aplicado';
    return null;
  };

  const discountText = discountLabel();
  const autoDiscountExists = getLocalStorage('discount') !== null;

  return (
    <div className="checkout-mobile-summary" data-testid="checkout-mobile-summary">
      <div className="checkout-mobile-summary__bar">
        <div className="checkout-mobile-summary__left">
          <div className="checkout-mobile-summary__totalRow">
            <span className="checkout-mobile-summary__totalLabel">Total</span>
            <span className="checkout-mobile-summary__totalValue">
              {formatMoney(primaryPaymentRow ? primaryPaymentRow.amount : totals.total)}
            </span>
          </div>
          <div className="checkout-mobile-summary__meta">
            <span className="checkout-mobile-summary__metaItem">{`Envío: ${shippingText}`}</span>
            {!!previewPaymentRows.length && (
              <span className="checkout-mobile-summary__metaItem">
                {previewPaymentRows.map((row) => (
                  <span key={row.key} className="checkout-mobile-summary__method">
                    <span className="checkout-mobile-summary__methodName">{row.label}</span>
                    <span className="checkout-mobile-summary__methodAmount">{formatMoney(row.amount)}</span>
                    {autoDiscountExists && (
                      <span className={`checkout-mobile-summary__methodNote ${row.isAutoAllowed ? 'is-ok' : 'is-no'}`}>
                        {row.isAutoAllowed ? 'incluye desc.' : 'sin desc.'}
                      </span>
                    )}
                  </span>
                ))}
              </span>
            )}
            {discountText && <span className="checkout-mobile-summary__metaItem checkout-mobile-summary__metaItem--discount">{discountText}</span>}
            {discountText && discountScopeNote && (
              <span className="checkout-mobile-summary__metaItem checkout-mobile-summary__metaItem--note">
                {discountScopeNote}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          className="checkout-mobile-summary__button"
          onClick={() => setOpen(true)}
        >
          Ver resumen
        </button>
      </div>

      {open && (
        <div className="checkout-mobile-summary__overlay" role="dialog" aria-modal="true">
          <button
            type="button"
            className="checkout-mobile-summary__backdrop"
            aria-label="Cerrar resumen"
            onClick={() => setOpen(false)}
          />
          <div className="checkout-mobile-summary__sheet">
            <div className="checkout-mobile-summary__sheetHeader">
              <div className="checkout-mobile-summary__sheetTitle">Resumen</div>
              <button
                type="button"
                className="checkout-mobile-summary__close"
                onClick={() => setOpen(false)}
              >
                Cerrar
              </button>
            </div>
            <div className="checkout-mobile-summary__sheetBody">
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CheckoutMobileSummaryBar.defaultProps = {
  coupon: null,
  subTotEnv: 0,
  paymentMethods: [],
};

CheckoutMobileSummaryBar.propTypes = {
  subtotal: PropTypes.number.isRequired,
  coupon: PropTypes.shape({
    percentage: PropTypes.number,
    amount: PropTypes.number,
  }),
  subTotEnv: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shippingText: PropTypes.string.isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    allow_discount: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  })),
  children: PropTypes.node.isRequired,
};

export default CheckoutMobileSummaryBar;
